import { Badge, Drawer, Flex, Image, Portal, Text } from '@chakra-ui/react';
import { formatPrice } from '@features/prediction-markets/fakeMarketData';
import { YesNo } from '@features/prediction-markets/OutcomeRow';
import {
  BuySellSegment,
  LimitPriceControl,
  OrderTypePicker,
  SharesControl,
  TradeButton,
  TradeSummary,
  YesNoButtons,
  useTradeForm,
} from '@features/prediction-markets/TradeFormControls';
import { MarketOutcome } from '@features/prediction-markets/types';
import { memo, useCallback } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outcome: MarketOutcome | null;
  commodityImage: string;
  question: string;
  yesNo: YesNo;
  onYesNoChange: (value: YesNo) => void;
}

const TradeDrawer = ({
  open,
  onOpenChange,
  outcome,
  commodityImage,
  question,
  yesNo,
  onYesNoChange,
}: Props) => {
  const form = useTradeForm();

  const handleDrawerOpenChange = useCallback(
    (e: { open: boolean }) => onOpenChange(e.open),
    [onOpenChange],
  );

  const handleToggleYesNo = useCallback(() => {
    onYesNoChange(yesNo === 'yes' ? 'no' : 'yes');
  }, [yesNo, onYesNoChange]);

  if (outcome == null) return null;

  const selectedPrice = yesNo === 'yes' ? outcome.yesPrice : outcome.noPrice;
  const { total, toWin } = form.computeSummary(outcome, yesNo);

  return (
    <Drawer.Root open={open} onOpenChange={handleDrawerOpenChange} placement="bottom">
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg="surface.background" borderTopRadius="2xl" maxH="85vh">
            <Drawer.Header px={4} py={3} borderBottomWidth="0">
              <Flex justifyContent="space-between" alignItems="center" w="full">
                <BuySellSegment side={form.side} onSideChange={form.handleSideChange} />
                <OrderTypePicker
                  orderType={form.orderType}
                  onOrderTypeChange={form.handleOrderTypeChange}
                />
              </Flex>
            </Drawer.Header>

            <Drawer.Body px={4} py={0} overflowY="auto">
              <Flex direction="column" gap={4}>
                <Flex alignItems="center" gap={3}>
                  <Image src={commodityImage} alt="" boxSize="36px" borderRadius="full" />
                  <Flex direction="column" gap={0.5}>
                    <Text fontSize="sm" fontWeight="semibold" color="text.primary" lineClamp={2}>
                      {question}
                    </Text>
                    <Flex alignItems="center" gap={2}>
                      <Text fontSize="xs" color="text.secondary">
                        {outcome.label}
                      </Text>
                      <Badge
                        size="sm"
                        px={1.5}
                        borderRadius="md"
                        bg={yesNo === 'yes' ? 'green.500' : 'red.500'}
                        color="white"
                        fontSize="2xs"
                        cursor="pointer"
                        onClick={handleToggleYesNo}
                      >
                        {yesNo === 'yes' ? 'Yes' : 'No'} {formatPrice(selectedPrice)}
                      </Badge>
                    </Flex>
                  </Flex>
                </Flex>

                <YesNoButtons outcome={outcome} yesNo={yesNo} onYesNoChange={onYesNoChange} />

                {form.orderType === 'limit' && (
                  <LimitPriceControl
                    limitPrice={form.limitPrice}
                    onInc={form.handleLimitPriceInc}
                    onDec={form.handleLimitPriceDec}
                  />
                )}

                <SharesControl
                  shares={form.shares}
                  onSharesChange={form.handleSharesChange}
                  onQuickShares={form.handleQuickShares}
                />

                <TradeSummary total={total} toWin={toWin} />
              </Flex>
            </Drawer.Body>

            <Drawer.Footer px={4} py={4} flexDirection="column" gap={2}>
              <TradeButton />
              <Text fontSize="2xs" color="text.secondary" textAlign="center">
                By trading, you agree to the Terms of Use.
              </Text>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default memo(TradeDrawer);
